import React, { memo, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  FormControl,
  Button,
  InputGroup,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  InputRightElement,
  Image,
} from "@chakra-ui/react";

import { useForm } from "react-hook-form";
import { InputControl, SelectControl } from "./formControls";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import vi from "date-fns/locale/vi";
import { clientAPI } from "../../api/client";
import { DataPlaceOfIdentification } from "../../utils/globalVariable";
import toast from "react-hot-toast";
import { settingCall } from "../../api/settings";
import { DataSetting } from "../../utils/contants";
import SearchableSelect from "../selectForm";
import { employeeCall } from "../../api/employee";
import { RiResetLeftFill } from "react-icons/ri";

registerLocale("vi", vi);

// Nơi cấp CCCD
const placeOfIdentificationOptions = DataPlaceOfIdentification.map((v) => ({
  value: v,
  label: v,
}));

const genderOptions = [
  { value: 0, label: "Nam" },
  { value: 1, label: "Nữ" },
  { value: 2, label: "Khác" },
];

const schema = Yup.object().shape({
  employeeId: Yup.string().required("Vui lòng chọn người đại diện"),
  code: Yup.string()
    .required("Mã nhân viên là bắt buộc")
    .test("is-unique", "Mã nhân viên đã tồn tại", async (value) => {
      if (!value) return true;
      return await employeeCall.checkCodeUnique(value);
    }),
  gender: Yup.number()
    .nullable()
    .transform((v, o) => (o === "" || o === null || o === undefined ? null : v))
    .required("Vui lòng chọn giới tính")
    .typeError("Vui lòng chọn giới tính"),
  fullName: Yup.string().required("Vui lòng nhập họ tên"),
  dateOfBirth: Yup.date()
    .nullable()
    .required("Vui lòng nhập ngày sinh")
    .transform((v, o) => (o === "" ? null : v))
    .typeError("Ngày sinh không hợp lệ"),
  email: Yup.string()
    .required("Vui lòng nhập email")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email không hợp lệ"),
  phone: Yup.string()
    .required("Vui lòng nhập số điện thoại")
    .matches(/^[0-9]{10,11}$/, "Số điện thoại không hợp lệ"),
  identificationNumber: Yup.string()
    .required("Vui lòng nhập số CCCD/CMND")
    .matches(/^\d{12}$/, "Số CCCD/CMND phải là 12 chữ số"),
  dateOfIdentification: Yup.date()
    .nullable()
    .required("Vui lòng nhập ngày cấp")
    .transform((v, o) => (o === "" ? null : v))
    .typeError("Ngày cấp không hợp lệ"),
  placeOfIdentification: Yup.string().required("Vui lòng chọn nơi cấp"),
  permanentAddress: Yup.string().required("Vui lòng nhập địa chỉ thường trú"),
  currentAddress: Yup.string().required("Vui lòng nhập chỗ ở hiện tại"),
  bankAccount: Yup.string().required("Vui lòng nhập số tài khoản"),
  taxCode: Yup.string().optional().nullable(),
});

export const FormRelative = memo(({ isOpen, onClose, onSubmit }) => {
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  // Danh sách nhân viên để chọn người đại diện
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [focusStates, setFocusStates] = useState({});
  const [zIndex, setZIndex] = useState("");
  
  // Load danh sách nhân viên (chỉ active, không phải CTV)
  useEffect(() => {
    if (!isOpen) return;
    const fetchEmployees = async () => {
      try {
        const data = await clientAPI(
          "get",
          `/employee/search?limit=9999&page=1&status=0`,
        );
        if (data?.items) {
          setEmployees(
            data.items
              .filter((e) => !e.isRelative)
              .map((emp) => ({
                key: emp._id,
                value: emp.fullName,
                code: emp.code,
              })),
          );
        }
      } catch (err) {
        console.error("Load employees error:", err);
      }
    };
    fetchEmployees();
  }, [isOpen]);

  // Reset form khi mở
  useEffect(() => {
    if (isOpen) {
      reset({});
      setSelectedEmployee(null);
      setFocusStates({});

      // Auto-fill mã CTV
      settingCall
        .get(DataSetting.latest_employee_id)
        .then((res) => {
          if (res?.items?.value) {
            setValue("code", `CTV00${Number(res.items.value) + 1}`);
          }
        })
        .catch(() => {});
    }
  }, [isOpen, reset]);

  const handleFocus = (field, zKey) => {
    if (zKey) setZIndex(zKey);
    setFocusStates((prev) => ({ ...prev, [field]: true }));
  };
  const handleBlur = (field) => {
    setZIndex("");
    setFocusStates((prev) => ({ ...prev, [field]: false }));
  };

  const handleDateChange = (date, name) => {
    setValue(name, date, { shouldValidate: true });
  };

  // Lấy mã CTV tự động tăng mới nhất
  const getDataLatestEmployeeId = async () => {
    const toastData = toast.loading("Đang lấy mã nhân viên mới nhất ...");
    try {
      const res = await settingCall.get(DataSetting.latest_employee_id);
      if (res?.items?.value) {
        setValue("code", `CTV00${Number(res.items.value) + 1}`, {
          shouldValidate: true,
        });
      }
      toast.success("Lấy thành công");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    toast.dismiss(toastData);
  };

  const submitHandler = (data) => {
    onSubmit(data);
  };

  const labelStyle = (field) => ({
    mb: "6px",
    fontSize: { base: "12px" },
    fontWeight: 400,
    lineHeight: "15px",
    color: focusStates[field]
      ? "var(--color-option-employee-hover)"
      : "var(--color-label-employee)",
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        padding={"24px"}
        h={"86.5vh"}
        w={"100%"}
        maxW={"625px"}
        overflow="auto"
      >
        <form onSubmit={handleSubmit(submitHandler)}>
          {/* Header */}
          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"10px"}
          >
            <Text
              color="var(--color-header-employee)"
              fontSize={"18px"}
              fontWeight={"600"}
            >
              Cộng tác viên mới
            </Text>
            <Box cursor={"pointer"} onClick={onClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#99A4B8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9L9 15"
                  stroke="#99A4B8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 9L15 15"
                  stroke="#99A4B8"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Flex>

          <Box
            w={"100%"}
            h={"1px"}
            backgroundColor={"var(--bg-line-employee)"}
            mt={"26px"}
            mb={"24px"}
          />

          <Box w={"100%"} maxW={"545px"} m={"0 auto"}>
            {/* === Người đại diện (admin only) === */}
            <Box mb={"20px"}>
              <Text
                fontSize={"16px"}
                lineHeight={"19px"}
                fontWeight={"600"}
                color={"var(--color-header-employee)"}
                mb={"16px"}
              >
                Người đại diện
              </Text>
              <FormControl>
                <Flex alignItems="center" gap="4px" mb={"6px"}>
                  <Text
                    fontSize={"12px"}
                    fontWeight={400}
                    lineHeight={"15px"}
                    color={"var(--color-label-employee)"}
                  >
                    Nhân viên đại diện
                  </Text>
                  <Text fontSize={"12px"} color={"red"} lineHeight={"15px"}>
                    *
                  </Text>
                </Flex>
                <input type="hidden" {...register("employeeId")} />
                <SearchableSelect
                  key={String(isOpen)}
                  isShowSearch={true}
                  options={employees}
                  initialSelectedOption={{ key: 0, value: "Tìm theo tên hoặc mã nhân viên..." }}
                  setValue={(key) => {
                    const emp = employees.find((e) => e.key === key);
                    setSelectedEmployee(emp || null);
                    setValue("employeeId", key || "", { shouldValidate: true });
                  }}
                  width={"100%"}
                  minW={"100%"}
                />
                {errors.employeeId && (
                  <Text fontSize={"12px"} color={"red"} mt={"4px"}>
                    {errors.employeeId.message}
                  </Text>
                )}
              </FormControl>
              {/* Hiển thị thông tin vùng/khối/chi nhánh sẽ kế thừa từ người đại diện */}
              {selectedEmployee && (
                <Text
                  fontSize={"11px"}
                  color={"var(--color-black-3)"}
                  mt={"8px"}
                  fontStyle={"italic"}
                >
                  Vùng, khối, chi nhánh sẽ được lấy tự động từ nhân viên đại
                  diện đã chọn.
                </Text>
              )}
            </Box>

            {/* === Thông tin cá nhân === */}
            <Box mb={"20px"}>
              <Text
                fontSize={"16px"}
                lineHeight={"19px"}
                fontWeight={"600"}
                color={"var(--color-header-employee)"}
                mb={"16px"}
              >
                Thông tin cá nhân
              </Text>

              {/* Mã nhân viên */}
              <Box mb={"12px"}>
                <Flex gap={"8px"} position={"relative"}>
                  <InputControl
                    label="Mã nhân viên"
                    placeholder="Nhập mã nhân viên"
                    errors={errors.code}
                    register={register("code")}
                    onFocus={() => handleFocus("code")}
                    onBlur={() => handleBlur("code")}
                    state={focusStates["code"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                  <Flex
                    mt={"6px"}
                    gap={"8px"}
                    cursor={"pointer"}
                    onClick={getDataLatestEmployeeId}
                    flexDirection={"column"}
                    position={"absolute"}
                    right={"0px"}
                    bottom={"0px"}
                    padding={"12px"}
                  >
                    <RiResetLeftFill
                      color="var(--color-option-employee-hover)"
                      size={"18px"}
                    />
                  </Flex>
                </Flex>
                <Flex mt={"6px"} gap={"8px"} flexDirection={"column"}>
                  <Text
                    fontSize={"12px"}
                    color="var(--color-option-employee-hover)"
                    fontStyle={"italic"}
                  >
                    Ấn vào nút để lấy mã nhân viên mới nhất
                  </Text>
                </Flex>
              </Box>

              {/* Họ tên | Giới tính */}
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"12px"}
              >
                <InputControl
                  label="Họ và tên"
                  placeholder="Nhập họ và tên"
                  errors={errors.fullName}
                  register={register("fullName")}
                  onFocus={() => handleFocus("fullName")}
                  onBlur={() => handleBlur("fullName")}
                  state={focusStates["fullName"]}
                  mwidth={"100%"}
                  isRequired={true}
                />
                <SelectControl
                  label="Giới tính"
                  dataOption={genderOptions}
                  selectedMain={watch("gender")}
                  placeholder="Chọn giới tính"
                  onChange={(opt) => {
                    setValue("gender", opt);
                    clearErrors("gender");
                  }}
                  onFocus={() => handleFocus("gender")}
                  onBlur={() => handleBlur("gender")}
                  state={focusStates["gender"]}
                  registerProps={register("gender")}
                  errorMessage={errors.gender}
                  isRequired={true}
                />
              </Flex>

              {/* Ngày sinh | Email */}
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"12px"}
              >
                <FormControl
                  flex={1}
                  minW={0}
                  zIndex={zIndex === "dateOfBirth" ? 3 : 0}
                >
                  <Flex alignItems="center" gap="4px" >
                    <Text {...labelStyle("dateOfBirth")}>Ngày sinh</Text>
                    <Text fontSize={"12px"} color={"red"} lineHeight={"15px"}>
                      *
                    </Text>
                  </Flex>
                  <InputGroup>
                    <DatePicker
                      selected={watch("dateOfBirth")}
                      onChange={(date) => handleDateChange(date, "dateOfBirth")}
                      dateFormat="dd/MM/yyyy"
                      locale="vi"
                      placeholderText="dd/mm/yyyy"
                      className="datePickerStyle"
                      onFocus={() => handleFocus("dateOfBirth", "dateOfBirth")}
                      onBlur={() => handleBlur("dateOfBirth")}
                      portalId="datepicker-portal"
                      popperProps={{ strategy: "fixed" }}
                      style={{ width: "100%" }}
                    />
                    <InputRightElement pointerEvents="none" h={"44px"}>
                      <Image src={calendarIcon} />
                    </InputRightElement>
                  </InputGroup>
                  {errors.dateOfBirth && (
                    <Text fontSize={"12px"} color={"red"}>
                      {errors.dateOfBirth.message}
                    </Text>
                  )}
                </FormControl>
                <Box flex={1} minW={0}>
                  <InputControl
                    type="email"
                    label="Email"
                    placeholder="Nhập email"
                    errors={errors.email}
                    register={register("email")}
                    onFocus={() => handleFocus("email")}
                    onBlur={() => handleBlur("email")}
                    state={focusStates["email"]}
                    mwidth={"100%"}
                    isRequired={true}
                  />
                </Box>
              </Flex>

              {/* Số điện thoại | Số CCCD/CMND */}
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"12px"}
              >
                <InputControl
                  label="Số điện thoại"
                  placeholder="Nhập số điện thoại"
                  errors={errors.phone}
                  register={register("phone")}
                  onFocus={() => handleFocus("phone")}
                  onBlur={() => handleBlur("phone")}
                  state={focusStates["phone"]}
                  mwidth={"100%"}
                  isRequired={true}
                />
                <InputControl
                  label="Số CCCD/CMND"
                  placeholder="Nhập 12 số CCCD/CMND"
                  errors={errors.identificationNumber}
                  register={register("identificationNumber")}
                  onFocus={() => handleFocus("identificationNumber")}
                  onBlur={() => handleBlur("identificationNumber")}
                  state={focusStates["identificationNumber"]}
                  mwidth={"100%"}
                  isRequired={true}
                />
              </Flex>

              {/* Ngày cấp | Nơi cấp */}
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"12px"}
              >
                <FormControl
                  flex={1}
                  minW={0}
                  zIndex={zIndex === "dateOfIdentification" ? 3 : 0}
                >
                  <Flex alignItems="center" gap="4px">
                    <Text {...labelStyle("dateOfIdentification")}>
                      Ngày cấp
                    </Text>
                    <Text fontSize={"12px"} color={"red"} lineHeight={"15px"}>
                      *
                    </Text>
                  </Flex>
                  <InputGroup>
                    <DatePicker
                      selected={watch("dateOfIdentification")}
                      onChange={(date) =>
                        handleDateChange(date, "dateOfIdentification")
                      }
                      dateFormat="dd/MM/yyyy"
                      locale="vi"
                      placeholderText="dd/mm/yyyy"
                      className="datePickerStyle"
                      onFocus={() =>
                        handleFocus(
                          "dateOfIdentification",
                          "dateOfIdentification",
                        )
                      }
                      onBlur={() => handleBlur("dateOfIdentification")}
                      portalId="datepicker-portal"
                      popperProps={{ strategy: "fixed" }}
                    />
                    <InputRightElement pointerEvents="none" h={"44px"}>
                      <Image src={calendarIcon} />
                    </InputRightElement>
                  </InputGroup>
                  {errors.dateOfIdentification && (
                    <Text fontSize={"12px"} color={"red"}>
                      {errors.dateOfIdentification.message}
                    </Text>
                  )}
                </FormControl>
                <Box flex={1} minW={0}>
                  <SelectControl
                    label="Nơi cấp"
                    dataOption={placeOfIdentificationOptions}
                    selectedMain={watch("placeOfIdentification")}
                    placeholder="Chọn nơi cấp"
                    onChange={(opt) => {
                      setValue("placeOfIdentification", opt);
                      clearErrors("placeOfIdentification");
                    }}
                    onFocus={() => handleFocus("placeOfIdentification")}
                    onBlur={() => handleBlur("placeOfIdentification")}
                    state={focusStates["placeOfIdentification"]}
                    registerProps={register("placeOfIdentification")}
                    errorMessage={errors.placeOfIdentification}
                    isRequired={true}
                  />
                </Box>
              </Flex>

              {/* Địa chỉ thường trú */}
              <Flex mb={"12px"}>
                <InputControl
                  label="Địa chỉ thường trú"
                  placeholder="Nhập địa chỉ thường trú"
                  errors={errors.permanentAddress}
                  register={register("permanentAddress")}
                  onFocus={() => handleFocus("permanentAddress")}
                  onBlur={() => handleBlur("permanentAddress")}
                  state={focusStates["permanentAddress"]}
                  mwidth={"100%"}
                  isRequired={true}
                />
              </Flex>

              {/* Chỗ ở hiện tại */}
              <Flex mb={"12px"}>
                <InputControl
                  label="Chỗ ở hiện tại"
                  placeholder="Nhập chỗ ở hiện tại"
                  errors={errors.currentAddress}
                  register={register("currentAddress")}
                  onFocus={() => handleFocus("currentAddress")}
                  onBlur={() => handleBlur("currentAddress")}
                  state={focusStates["currentAddress"]}
                  mwidth={"100%"}
                  isRequired={true}
                />
              </Flex>

              {/* Mã số thuế | Số tài khoản */}
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
              >
                <InputControl
                  label="Mã số thuế"
                  placeholder="Nhập mã số thuế"
                  errors={errors.taxCode}
                  register={register("taxCode")}
                  onFocus={() => handleFocus("taxCode")}
                  onBlur={() => handleBlur("taxCode")}
                  state={focusStates["taxCode"]}
                  mwidth={"100%"}
                />
                <InputControl
                  label="Số tài khoản"
                  placeholder="Nhập số tài khoản"
                  errors={errors.bankAccount}
                  register={register("bankAccount")}
                  onFocus={() => handleFocus("bankAccount")}
                  onBlur={() => handleBlur("bankAccount")}
                  state={focusStates["bankAccount"]}
                  mwidth={"100%"}
                  isRequired={true}
                />
              </Flex>
            </Box>

            {/* Ghi chú */}
            <Box
              p={"12px 16px"}
              borderRadius={"8px"}
              bg={"var(--bg-form-employee)"}
              mb={"8px"}
            >
              <Text
                fontSize={"12px"}
                color={"var(--color-black-4)"}
                lineHeight={"18px"}
              >
                <strong>Lưu ý:</strong> Vùng, khối, chi nhánh của cộng tác viên
                sẽ tự động kế thừa từ nhân viên đại diện đã chọn. Mật khẩu mặc
                định sẽ được gán tự động.
              </Text>
            </Box>
          </Box>

          {/* Submit */}
          <Flex justifyContent={"flex-end"} mt={"24px"} w={"100%"}>
            <Button
              type="submit"
              borderRadius={"8px"}
              background={"var(--linear-gradient-employee)"}
              color={"var(--color-main)"}
              w={"200px"}
              h={"44px"}
              _hover={{ filter: "brightness(90%)" }}
            >
              Thêm cộng tác viên
            </Button>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
});

export default FormRelative;
